import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
/** Um clique percorre estas velocidades e volta ao começo. */
const VELOCIDADES = [1, 1.5, 2];
/**
 * Tocador de áudio do chat.
 *
 * O `<audio controls>` do navegador traz volume — que num recado de voz não serve para
 * nada, porque quem ouve já mexe no volume do aparelho — e some com a barra de tempo em
 * telas estreitas, justamente onde o áudio mais aparece. Aqui ficam só as três coisas que
 * se usa de verdade: tocar, arrastar para qualquer ponto e acelerar.
 *
 * A barra é um `range`: arrastar, clicar no meio e usar as setas do teclado funcionam sem
 * nada escrito, e leitor de tela anuncia como controle deslizante.
 */
export function TocadorAudio({ url }) {
    const audio = useRef(null);
    const [tocando, setTocando] = useState(false);
    const [posicao, setPosicao] = useState(0);
    const [duracao, setDuracao] = useState(0);
    const [velocidade, setVelocidade] = useState(1);
    useEffect(() => {
        const el = audio.current;
        if (!el)
            return;
        const aoTempo = () => setPosicao(el.currentTime);
        // A duração pode chegar depois (e como Infinity em stream): só vale quando é número.
        const aoCarregar = () => setDuracao(Number.isFinite(el.duration) ? el.duration : 0);
        const aoFim = () => {
            setTocando(false);
            setPosicao(0);
            el.currentTime = 0;
        };
        el.addEventListener('timeupdate', aoTempo);
        el.addEventListener('loadedmetadata', aoCarregar);
        el.addEventListener('durationchange', aoCarregar);
        el.addEventListener('ended', aoFim);
        return () => {
            el.removeEventListener('timeupdate', aoTempo);
            el.removeEventListener('loadedmetadata', aoCarregar);
            el.removeEventListener('durationchange', aoCarregar);
            el.removeEventListener('ended', aoFim);
        };
    }, []);
    function tocarOuPausar() {
        const el = audio.current;
        if (!el)
            return;
        if (el.paused) {
            void el.play().then(() => setTocando(true));
        }
        else {
            el.pause();
            setTocando(false);
        }
    }
    function trocarVelocidade() {
        const proxima = VELOCIDADES[(VELOCIDADES.indexOf(velocidade) + 1) % VELOCIDADES.length];
        setVelocidade(proxima);
        if (audio.current)
            audio.current.playbackRate = proxima;
    }
    function irPara(segundos) {
        setPosicao(segundos);
        if (audio.current)
            audio.current.currentTime = segundos;
    }
    const progresso = duracao > 0 ? (posicao / duracao) * 100 : 0;
    return (_jsxs("div", { className: "mb-1 flex w-60 max-w-full items-center gap-2 rounded-full bg-black/10 px-2 py-1.5", children: [_jsx("audio", { ref: audio, src: url, preload: "metadata", className: "hidden" }), _jsx("button", { type: "button", onClick: tocarOuPausar, "aria-label": tocando ? 'Pausar' : 'Tocar', className: "shrink-0 rounded-full p-1 hover:bg-black/10", children: tocando ? (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: [_jsx("rect", { x: "6", y: "5", width: "4", height: "14", rx: "1" }), _jsx("rect", { x: "14", y: "5", width: "4", height: "14", rx: "1" })] })) : (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M8 5.14v13.72a1 1 0 0 0 1.54.84l10.3-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" }) })) }), _jsx("input", { type: "range", min: 0, max: duracao || 0, step: 0.1, value: posicao, onChange: (e) => irPara(Number(e.target.value)), "aria-label": "Posi\u00E7\u00E3o do \u00E1udio", 
                // O preenchimento é pintado no próprio fundo: assim a parte já ouvida aparece sem
                // um segundo elemento por cima, que atrapalharia o arraste.
                style: {
                    background: `linear-gradient(to right, currentColor ${progresso}%, rgba(0,0,0,0.2) ${progresso}%)`,
                }, className: "h-1 min-w-0 flex-1 cursor-pointer appearance-none rounded-full [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-current" }), _jsx("span", { className: "shrink-0 font-mono text-[11px] tabular-nums opacity-80", children: relogio(duracao > 0 && posicao > 0 ? duracao - posicao : duracao) }), _jsxs("button", { type: "button", onClick: trocarVelocidade, "aria-label": `Velocidade ${velocidade}x — clique para mudar`, className: "shrink-0 rounded px-1 font-mono text-[11px] hover:bg-black/10", children: [velocidade, "x"] })] }));
}
/** Segundos viram m:ss. Áudio de chat não passa de 5 minutos (o gravador corta ali). */
function relogio(segundos) {
    if (!Number.isFinite(segundos) || segundos <= 0)
        return '0:00';
    const min = Math.floor(segundos / 60);
    return `${min}:${String(Math.floor(segundos % 60)).padStart(2, '0')}`;
}
//# sourceMappingURL=TocadorAudio.js.map